import {
  type ChangeEvent,
  type FocusEvent,
  type KeyboardEvent,
  type MouseEvent,
  type ReactElement,
  useCallback,
  useRef,
  useState,
} from 'react';
import { CircularProgress, InputBase } from '@mui/material';
import { Box, Button } from 'components/ui';
import type { DashboardPromptInputProps } from './DashboardPromptInput.props';
import {
  promptActionsSx,
  promptBusyHintSx,
  promptBusySpinnerSx,
  promptCardSx,
  promptIconSx,
  promptInputSx,
  promptSubmitButtonSx,
  promptSubmitHintSx,
} from './DashboardPromptInput.styles';

const SCALAR_AI_ICON_SRC = '/images/scalar_ai.png';

// The hero prompt input for a dashboard summary tab. Composed from InputBase rather than TextField
// (or the components/ui Input wrapper) so the border and radius live on the outer Box: MUI centers
// adornments unconditionally, which would drop the AI icon and the submit hint to the middle of the
// box as the textarea grows.
const DashboardPromptInput = ({
  autoFocus = false,
  busyHint,
  initialValue = '',
  inputRef,
  isBusy = false,
  isDisabled = false,
  maxRows,
  onCancel,
  onSubmit,
  placeholder,
  submitHint,
  submitLabel,
  testIds,
}: DashboardPromptInputProps): ReactElement => {
  const [value, setValue] = useState(initialValue);
  const isLocked = isDisabled || isBusy;
  const trimmedValue = value.trim();
  // Only so handleBlur can tell "focus left the editor" from "focus moved to the submit button".
  const cardRef = useRef<HTMLDivElement>(null);

  const handleChange = useCallback((event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setValue(event.target.value);
  }, []);

  // Shared by the Enter key and the submit button, so the two paths can never drift on the trim,
  // the lock check, or the clear-on-success rule.
  const submitPrompt = useCallback(async () => {
    const trimmedPrompt = value.trim();
    if (!trimmedPrompt || isLocked) {
      return;
    }

    // Clear only on a confirmed success, so a rejected prompt stays editable rather than
    // forcing the user to retype it.
    const didSucceed = await onSubmit?.(trimmedPrompt);
    if (didSucceed === true) {
      setValue('');
    }
  }, [isLocked, onSubmit, value]);

  // Clicking away dismisses the editor, same as Escape. Skipped while busy: disabling the field
  // blurs it in most browsers, and collapsing then would discard the text a failure needs to keep.
  const handleBlur = useCallback(
    (event: FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      if (isBusy) {
        return;
      }

      // Tabbing to the submit button is not leaving the editor. Without this the prompt bar would
      // collapse on that Tab and unmount the button before it could be pressed.
      if (cardRef.current?.contains(event.relatedTarget)) {
        return;
      }

      onCancel?.();
    },
    [isBusy, onCancel]
  );

  // Enter submits, Shift+Enter inserts a newline — same contract as the toolbar AIAssistantInput,
  // which is what makes the "Press ↵" hint honest.
  const handleKeyDown = useCallback(
    async (event: KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      if (event.key === 'Escape' && onCancel) {
        // Stop it here so the key does not also reach an enclosing dialog or menu.
        event.stopPropagation();
        onCancel();
        return;
      }

      if (event.key !== 'Enter' || event.shiftKey) {
        return;
      }

      // Synchronous, before any await: React pools nothing here, but the newline must be suppressed
      // on this tick regardless of what the handler does next.
      event.preventDefault();
      await submitPrompt();
    },
    [onCancel, submitPrompt]
  );

  // The press must not blur the textarea: the prompt bar dismisses on blur, so a focus change here
  // would unmount this button between mousedown and click and swallow the submission.
  const handleSubmitMouseDown = useCallback((event: MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
  }, []);

  return (
    <Box ref={cardRef} sx={promptCardSx}>
      <Box aria-hidden alt="" component="img" src={SCALAR_AI_ICON_SRC} sx={promptIconSx} />
      <InputBase
        autoFocus={autoFocus}
        disabled={isLocked}
        inputProps={testIds ? { 'data-testid': testIds.input } : undefined}
        inputRef={inputRef}
        maxRows={maxRows}
        minRows={1}
        multiline
        onBlur={handleBlur}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        sx={promptInputSx}
        value={value}
      />
      <Box sx={promptActionsSx}>
        {isBusy && Boolean(busyHint) ? (
          <Box
            aria-live="polite"
            component="span"
            data-testid={testIds?.submitHint}
            sx={promptBusyHintSx}>
            <CircularProgress size={12} sx={promptBusySpinnerSx} thickness={5} />
            {busyHint}
          </Box>
        ) : (
          Boolean(submitHint) && (
            <Box component="span" data-testid={testIds?.submitHint} sx={promptSubmitHintSx}>
              {submitHint}
            </Box>
          )
        )}
        {Boolean(submitLabel) && (
          // Disabled on an empty prompt rather than submitting a no-op, matching what Enter does.
          // The real attribute, so pointer and Enter/Space are blocked together.
          <Button
            data-testid={testIds?.submitButton}
            disabled={isLocked || !trimmedValue}
            onClick={submitPrompt}
            onMouseDown={handleSubmitMouseDown}
            sx={promptSubmitButtonSx}
            type="button"
            visualVariant="brand">
            {submitLabel}
          </Button>
        )}
      </Box>
    </Box>
  );
};

export default DashboardPromptInput;
